import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, CreditCard, Cpu, Users, Wallet, Shield, LayoutDashboard, Banknote, Smartphone, MessageSquare, Gift } from "lucide-react";
import SEO from "../components/SEO";

const MODULE_ICONS = [CreditCard, Cpu, Users, Wallet, Shield, LayoutDashboard, Banknote, Smartphone, MessageSquare, Gift];

const ProductPage = () => {
  const { t } = useTranslation();
  const advantage = t("product.advantage", { returnObjects: true });
  const modules = t("product.modules.items", { returnObjects: true });

  return (
    <div data-testid="product-page">
      <SEO
        path="/producto"
        title={t("seo.product.title")}
        description={t("seo.product.description")}
        keywords={t("seo.product.keywords")}
        schema={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": t("brand.name") + " — Card Issuing & Processing Platform",
          "category": "Card Issuing Platform",
          "brand": { "@type": "Brand", "name": t("brand.name") },
          "description": t("seo.product.description")
        }}
      />
      {/* Hero */}
      <section className="hero" style={styles.hero}>
        <div className="container">
          <div className="heroInner" style={styles.heroInner}>
            <div className="eyebrow" style={styles.eyebrow}>{t("product.hero.eyebrow")}</div>
            <h1 className="heroTitle" style={styles.heroTitle}>
              {t("product.hero.titleLine1")}<br />{t("product.hero.titleLine2")}
            </h1>
            <p className="heroSubtitle" style={styles.heroSubtitle}>{t("product.hero.subtitle")}</p>
            <div className="heroCtaRow" style={styles.heroCtaRow}>
              <Link to="/contacto">
                <button className="btn-primary" data-testid="product-hero-cta">
                  {t("common.scheduleADemo")}
                  <ArrowRight size={18} style={{ marginLeft: 8 }} />
                </button>
              </Link>
              <Link to="/integracion" className="secondaryLink" style={styles.secondaryLink} data-testid="product-hero-link-integration">
                {t("common.viewIntegration")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Advantage strip */}
      <section className="advantage" style={styles.advantage}>
        <div className="container">
          <div className="advantageGrid" style={styles.advantageGrid}>
            {advantage.map((a, i) => (
              <div key={i} className="advantageItem" style={styles.advantageItem}>
                <div className="advantageNumber" style={styles.advantageNumber}>{a.number}</div>
                <div className="advantageLabel" style={styles.advantageLabel}>{a.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="modulesSection" style={styles.modulesSection}>
        <div className="container">
          <div className="sectionHeader" style={styles.sectionHeader}>
            <h2 className="sectionTitle" style={styles.sectionTitle}>{t("product.modules.titleLine1")}<br />{t("product.modules.titleLine2")}</h2>
            <p className="sectionSubtitle" style={styles.sectionSubtitle}>{t("product.modules.subtitle")}</p>
          </div>

          <div className="modulesGrid" style={styles.modulesGrid}>
            {modules.map((m, idx) => {
              const Icon = MODULE_ICONS[idx] || CreditCard;
              return (
                <div
                  key={m.num}
                  className="stack-card moduleCard"
                  style={styles.moduleCard}
                  data-testid={`product-module-${m.num}`}
                >
                  <div className="moduleHead" style={styles.moduleHead}>
                    <div className="stack-number moduleNumber" style={styles.moduleNumber}>{m.num}</div>
                    <div className="moduleIconWrap" style={styles.moduleIconWrap}>
                      <Icon size={22} strokeWidth={1.75} />
                    </div>
                  </div>
                  <h3 className="stack-title moduleTitle" style={styles.moduleTitle}>{m.title}</h3>
                  <p className="stack-description moduleSubtitle" style={styles.moduleSubtitle}>{m.subtitle}</p>
                  <ul className="moduleList" style={styles.moduleList}>
                    {m.items.map((it, i) => (
                      <li key={i} className="stack-description moduleItem" style={styles.moduleItem}>
                        <span className="moduleBullet" style={styles.moduleBullet}>—</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ctaSection" style={styles.ctaSection}>
        <div className="container">
          <div className="ctaInner" style={styles.ctaInner}>
            <h2 className="ctaTitle" style={styles.ctaTitle}>{t("product.cta.title")}</h2>
            <p className="ctaSubtitle" style={styles.ctaSubtitle}>{t("product.cta.subtitle")}</p>
            <Link to="/contacto">
              <button className="btn-primary ctaButton" style={styles.ctaButton} data-testid="product-final-cta">
                {t("common.scheduleADemo")}
                <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  hero: {
    padding: "160px 20px 80px",
    background: "#ffffff"
  },
  heroInner: {
    maxWidth: "900px",
    margin: "0 auto",
    textAlign: "left"
  },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#f5576c",
    marginBottom: "20px"
  },
  heroTitle: {
    fontSize: "64px",
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: "-0.03em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "24px"
  },
  heroSubtitle: {
    fontSize: "20px",
    lineHeight: 1.6,
    color: "#4a5568",
    maxWidth: "720px",
    marginBottom: "36px"
  },
  heroCtaRow: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    flexWrap: "wrap"
  },
  secondaryLink: {
    color: "#667eea",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "16px"
  },
  advantage: {
    padding: "48px 20px",
    background: "#f8f9fc",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    borderBottom: "1px solid rgba(0,0,0,0.06)"
  },
  advantageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "32px",
    maxWidth: "1100px",
    margin: "0 auto"
  },
  advantageItem: {
    textAlign: "left"
  },
  advantageNumber: {
    fontSize: "28px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "-0.02em",
    marginBottom: "6px"
  },
  advantageLabel: {
    fontSize: "14px",
    color: "#718096",
    fontWeight: 500
  },
  modulesSection: {
    padding: "100px 20px",
    background: "#ffffff"
  },
  sectionHeader: {
    textAlign: "center",
    maxWidth: "760px",
    margin: "0 auto 60px"
  },
  sectionTitle: {
    fontSize: "52px",
    fontWeight: 700,
    lineHeight: 1.1,
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "16px",
    letterSpacing: "-0.02em"
  },
  sectionSubtitle: {
    fontSize: "18px",
    color: "#4a5568",
    lineHeight: 1.6
  },
  modulesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "28px",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  moduleCard: {
    padding: "36px",
    background: "#ffffff",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.04)"
  },
  moduleHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px"
  },
  moduleNumber: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#667eea",
    letterSpacing: "0.05em"
  },
  moduleIconWrap: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "#f1f2f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667eea"
  },
  moduleTitle: {
    fontSize: "26px",
    fontWeight: 700,
    color: "#1a202c",
    marginBottom: "6px",
    letterSpacing: "-0.01em"
  },
  moduleSubtitle: {
    fontSize: "15px",
    color: "#718096",
    marginBottom: "22px"
  },
  moduleList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  moduleItem: {
    display: "flex",
    gap: "10px",
    fontSize: "15px",
    lineHeight: 1.55,
    color: "#4a5568"
  },
  moduleBullet: {
    color: "#f5576c",
    fontWeight: 700
  },
  ctaSection: {
    padding: "100px 20px",
    background: "#ffffff",
    borderTop: "1px solid rgba(0, 0, 0, 0.08)"
  },
  ctaInner: {
    maxWidth: "700px",
    margin: "0 auto",
    textAlign: "center"
  },
  ctaTitle: {
    fontSize: "52px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "20px"
  },
  ctaSubtitle: {
    fontSize: "19px",
    color: "#4a5568",
    lineHeight: 1.6,
    marginBottom: "36px"
  },
  ctaButton: {
    fontSize: "17px",
    padding: "16px 36px"
  }
};

// Responsive
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 1024px) {
    [data-testid="product-page"] div[style*="grid-template-columns: repeat(2, 1fr)"][style*="max-width: 1200px"] { grid-template-columns: 1fr !important; }
    [data-testid="product-page"] div[style*="grid-template-columns: repeat(4, 1fr)"][style*="max-width: 1100px"] { grid-template-columns: repeat(2, 1fr) !important; }
    [data-testid="product-page"] h1 { font-size: 44px !important; }
    [data-testid="product-page"] h2 { font-size: 38px !important; }
  }
  @media (max-width: 640px) {
    [data-testid="product-page"] section:first-child { padding: 120px 16px 60px !important; }
    [data-testid="product-page"] h1 { font-size: 34px !important; }
    [data-testid="product-page"] div[style*="grid-template-columns: repeat(4, 1fr)"][style*="max-width: 1100px"] { grid-template-columns: 1fr !important; }
    [data-testid="product-page"] h2 { font-size: 30px !important; }
    [data-testid^="product-module-"] { padding: 26px !important; }
  }
`;
document.head.appendChild(styleSheet);

export default ProductPage;
